/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   shouldSyncMapChange thunk
 *                Determines if our application is in a state that cares about map change
 */

import { memoizeState } from '@zcode/memoizers'
import { ZOOM_HOOD, isValidBounds, similarValues } from '@zcode/nr-utils'
import type { State } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import type { RouteComponentProps } from 'react-router-dom'
import { checkMainView } from '../../../inspectors'
import { getPrecisionFromZoom } from '../../helpers/getPrecisionFromZoom'
import { selectBox, selectCenter, selectZoom } from '../../location/selectors'
import type { Dispatch } from '../../types'
import {
  selectMapBox,
  selectMapCenter,
  selectMapReady,
  selectMapSyncDisabled,
  selectMapSyncOnMove,
  selectMapUnsynced,
  selectMapZoom,
} from '../selectors'

const isCorrectView = (routeProps: RouteComponentProps) => {
  return checkMainView(routeProps)
}

const isRentReportOpen = (routeProps: RouteComponentProps) => {
  return routeProps.location.hash === '#rent-report'
}

const isMapSyncEnabled = (state: State) => {
  const syncDisabled = selectMapSyncDisabled(state)
  const syncOnMove = selectMapSyncOnMove(state)

  return syncOnMove && !syncDisabled
}

const isMapReadyForSync = (state: State) => {
  const isMapReady = selectMapReady(state)
  const center = selectCenter(state)

  return isMapReady && isNumber(center.lat) && isNumber(center.lng)
}

export const isBoxChanged = memoizeState((state: State) => {
  const mapBox = selectMapBox(state)
  const mapZoom = selectMapZoom(state)

  const box = selectBox(state)

  const hasValidMapBox = isValidBounds(mapBox)
  const hasValidBox = isValidBounds(box)
  const precision = getPrecisionFromZoom(mapZoom)

  return hasValidMapBox && (!hasValidBox || !similarValues(box, mapBox, precision))
})

export const isCenterChanged = memoizeState((state: State) => {
  const mapCenter = selectMapCenter(state)
  const mapZoom = selectMapZoom(state)

  const center = selectCenter(state)
  const precision = getPrecisionFromZoom(mapZoom)

  return !similarValues(center, mapCenter, precision)
})

export const zoomChangedToHoodZoom = memoizeState((state: State) => {
  const mapReady = selectMapReady(state)
  const mapZoom = selectMapZoom(state)
  const isHoodZoom = mapZoom >= ZOOM_HOOD
  const zoom = selectZoom(state)

  return isHoodZoom && (zoom ?? 0) < ZOOM_HOOD && mapReady
})

const isMapSyncNecessary = memoizeState((state: State) => {
  // has has moved condition
  const unsynced = selectMapUnsynced(state)

  // map has moved "enough" condition
  const shouldUpdateBox = isBoxChanged(state)
  const shouldUpdateCenter = isCenterChanged(state)
  const becameHoodZoom = zoomChangedToHoodZoom(state)

  return unsynced && (shouldUpdateBox || shouldUpdateCenter || becameHoodZoom)
})

export const shouldSyncMapChange =
  (routeProps: RouteComponentProps, skipSyncCheck?: boolean) =>
  (dispatch: Dispatch, getState: () => State) => {
    if (!isCorrectView(routeProps)) {
      return false
    }

    if (isRentReportOpen(routeProps)) {
      return false
    }

    const state = getState()

    if (!isMapSyncEnabled(state) && !skipSyncCheck) {
      return false
    }

    if (!isMapReadyForSync(state)) {
      return false
    }

    if (!isMapSyncNecessary(state)) {
      return false
    }

    return true
  }
